/**
 * Consulting: the clock, the time it produced, and the invoices raised from it.
 *
 * The page is laid out in the order the work happens — record, review, bill —
 * and shows the same two tables the invoice does, so what is on screen and what
 * comes out of the printer are recognisably the same document.
 *
 * The list defaults to what has not been billed yet. Everything already on an
 * invoice is one filter away rather than mixed in: after a few months the
 * archived rows outnumber the open ones many times over, and the question this
 * screen exists to answer is "what do I still need to bill".
 *
 * Every write refetches rather than patching state in place. The server owns
 * the arithmetic — rounding moves an end time, a rate change can merge two rows
 * into one — and a client that tried to mirror those rules would eventually
 * disagree with the invoice.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type {
  BillTo,
  ConsultingClient,
  EntriesResponse,
  EntryFilter,
  RunningTimer,
} from '@/api/types'
import { ConsultingDayRow } from '@/components/ConsultingDayRow'
import { ConsultingInvoiceDialog } from '@/components/ConsultingInvoiceDialog'
import { ConsultingSummary } from '@/components/ConsultingSummary'
import { ConsultingTimer } from '@/components/ConsultingTimer'
import { useToast } from '@/components/Toast'
import { EmptyState, ErrorState, Field, LoadingBlock } from '@/components/ui'
import { formatMoney, monthStartIso, selectedDays, todayIso, trimHours } from '@/lib/consulting'
import { pluralize } from '@/lib/format'

const EMPTY: EntriesResponse = { days: [], summary: [], total_hours: 0, total_amount: 0 }

const FILTERS: { value: EntryFilter; label: string }[] = [
  { value: 'open', label: 'Not billed' },
  { value: 'invoiced', label: 'Archived' },
  { value: 'all', label: 'Everything' },
]

export function ConsultingPage() {
  const toast = useToast()
  const [entries, setEntries] = useState<EntriesResponse>(EMPTY)
  const [clients, setClients] = useState<ConsultingClient[]>([])
  const [billTos, setBillTos] = useState<BillTo[]>([])
  const [timer, setTimer] = useState<RunningTimer>({
    segment: null,
    client_id: null,
    client_name: null,
    day_id: null,
    work_date: null,
    rate_kind: null,
    rate: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [start, setStart] = useState(monthStartIso)
  const [end, setEnd] = useState(todayIso)
  const [clientId, setClientId] = useState<number | ''>('')
  const [filter, setFilter] = useState<EntryFilter>('open')

  const [selection, setSelection] = useState<ReadonlySet<number>>(new Set())
  const [dialogOpen, setDialogOpen] = useState(false)

  const queryParams = useMemo(
    () => ({
      start: start || undefined,
      end: end || undefined,
      client_id: clientId === '' ? undefined : clientId,
      archived: filter,
    }),
    [start, end, clientId, filter],
  )

  const load = useCallback(async () => {
    try {
      const [nextEntries, nextTimer] = await Promise.all([
        api.consulting.entries(queryParams),
        api.consulting.timer.read(),
      ])
      setEntries(nextEntries)
      setTimer(nextTimer)
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [queryParams])

  // Clients and bill-tos change far less often than time does, so they load
  // once rather than on every filter change.
  useEffect(() => {
    async function loadPickers() {
      try {
        const [nextClients, nextBillTos] = await Promise.all([
          api.consulting.clients.list(),
          api.consulting.billTos.list(),
        ])
        setClients(nextClients)
        setBillTos(nextBillTos)
      } catch (caught) {
        if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
      }
    }
    void loadPickers()
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /** Run a write, refetch, and let the server's own message explain a refusal. */
  const act = useCallback(
    async (action: () => Promise<unknown>, success?: string) => {
      try {
        await action()
        await load()
        if (success) toast.success(success)
      } catch (caught) {
        toast.error(caught instanceof ApiError ? caught.message : 'That did not work.')
        // Refetch even on failure: a 409 usually means this screen is looking
        // at something that has since changed.
        await load()
      }
    },
    [load, toast],
  )

  function toggle(id: number, on: boolean) {
    setSelection((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const billable = entries.days.filter((day) => day.invoice_id === null)
  const chosen = selectedDays(entries.days, selection)
  const allSelected = billable.length > 0 && chosen.length === billable.length

  const chosenHours = Number(chosen.reduce((sum, day) => sum + day.hours, 0).toFixed(2))
  const chosenAmount = Number(
    chosen.reduce((sum, day) => sum + (day.amount ?? 0), 0).toFixed(2),
  )

  if (loading) return <LoadingBlock label="Loading consulting" />
  if (error) return <ErrorState message={error} onRetry={() => void load()} />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-bold">Consulting</h1>
        <div className="ml-auto flex gap-2">
          <Link to="/consulting/clients" className="btn btn-neutral btn-sm">
            Clients
          </Link>
          <Link to="/consulting/invoices" className="btn btn-neutral btn-sm">
            Invoices
          </Link>
        </div>
      </div>

      {clients.length === 0 ? (
        <EmptyState
          title="No clients yet"
          action={
            <Link to="/consulting/clients" className="btn btn-primary">
              Add a client
            </Link>
          }
        >
          A client is whatever the work is recorded against — a company, a matter, or Misc for
          the things that are neither. Every one of them bills at $250 an hour unless you say
          otherwise.
        </EmptyState>
      ) : (
        <ConsultingTimer
          clients={clients}
          timer={timer}
          onStart={(id, note, rate) =>
            act(() => api.consulting.timer.start(id, note, rate))
          }
          onStop={(note) => act(() => api.consulting.timer.stop(note), 'Stopped.')}
          // Not routed through `act`: a refusal has to reach the form so it can
          // stay open with the times still in it.
          onRecord={async (payload) => {
            await api.consulting.segments.create(payload)
            await load()
            toast.success('Recorded.')
          }}
        />
      )}

      <section className="card flex flex-wrap items-end gap-2 p-3">
        <Field label="From" className="w-40">
          <input
            type="date"
            className="field"
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
        </Field>
        <Field label="To" className="w-40">
          <input
            type="date"
            className="field"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
        </Field>
        <Field label="Client" className="w-48">
          <select
            className="field"
            value={clientId}
            onChange={(event) => setClientId(event.target.value ? Number(event.target.value) : '')}
          >
            <option value="">Everyone</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Showing" className="w-40">
          <select
            className="field"
            value={filter}
            onChange={(event) => setFilter(event.target.value as EntryFilter)}
          >
            {FILTERS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </Field>
        <a
          className="btn btn-neutral btn-sm ml-auto"
          href={api.consulting.exportUrl(queryParams)}
        >
          Export CSV
        </a>
      </section>

      <ConsultingSummary
        rows={entries.summary}
        totalHours={entries.total_hours}
        totalAmount={entries.total_amount}
        caption={`${entries.days.length} ${pluralize(entries.days.length, 'row')} shown`}
      />

      <section className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2.5">
          <h2 className="text-sm font-semibold">Detail</h2>
          {billable.length > 0 && (
            <button
              type="button"
              className="text-xs font-semibold text-navy hover:underline"
              onClick={() =>
                setSelection(allSelected ? new Set() : new Set(billable.map((day) => day.id)))
              }
            >
              {allSelected ? 'Clear selection' : 'Select all billable'}
            </button>
          )}
        </div>

        {entries.days.length === 0 ? (
          <EmptyState title="Nothing here yet">
            {filter === 'open'
              ? 'Nothing tracked in this range that has not been billed. Start the timer, or widen the dates.'
              : 'No time in this range. Try widening the dates.'}
          </EmptyState>
        ) : (
          <ul className="m-0 list-none p-0">
            {entries.days.map((day) => (
              <ConsultingDayRow
                key={day.id}
                day={day}
                clients={clients}
                selected={selection.has(day.id)}
                onSelect={toggle}
                onPatch={(id, patch) => act(() => api.consulting.days.update(id, patch))}
                onDelete={(id) => act(() => api.consulting.days.remove(id), 'Deleted.')}
                onUnarchive={(id) =>
                  act(() => api.consulting.days.unarchive(id), 'Back on the open list.')
                }
                onPatchSegment={(id, patch) =>
                  act(() => api.consulting.segments.update(id, patch))
                }
                onDeleteSegment={(id) =>
                  act(() => api.consulting.segments.remove(id), 'Deleted.')
                }
              />
            ))}
          </ul>
        )}
      </section>

      {/* The selection bar sits above the bottom edge rather than in the flow:
          the rows being counted are what you are looking at while you tick
          them, and a footer at the end of a long table is off screen. */}
      {chosen.length > 0 && (
        <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-navy bg-surface px-3 py-2.5 shadow-lg">
          <span className="text-sm font-semibold">
            {chosen.length} {pluralize(chosen.length, 'row')}
          </span>
          <span className="text-sm text-ink-3">
            {trimHours(chosenHours)} {pluralize(chosenHours, 'hour')} &middot;{' '}
            {formatMoney(chosenAmount)}
          </span>
          <button
            type="button"
            className="btn btn-neutral btn-sm ml-auto"
            onClick={() => setSelection(new Set())}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setDialogOpen(true)}
          >
            Create invoice
          </button>
        </div>
      )}

      <ConsultingInvoiceDialog
        open={dialogOpen}
        days={chosen}
        billTos={billTos}
        onClose={() => setDialogOpen(false)}
        onCreate={async (payload) => {
          await api.consulting.invoices.create(payload)
          setSelection(new Set())
          await load()
          toast.success('Invoice created. It is under Invoices.')
        }}
      />
    </div>
  )
}
